import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import CircularProgress from '@material-ui/core/CircularProgress';
import CssBaseline from '@material-ui/core/CssBaseline';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import { Button, FormControl, Grid, InputLabel, Link, makeStyles, MenuItem, Select, TextField } from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { languageActions, sessionActions } from '../../store';
import { t, languages } from '../../common/LocalizationProvider';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    height: 'auto',
    backgroundColor: '#020408',
    backgroundImage: 'radial-gradient(circle at 50% 50%, #051026 0%, #010307 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflowX: 'hidden',
    overflowY: 'auto',
    padding: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
  },
  backgroundEcosystem: {
    position: 'absolute',
    width: '120%',
    height: '120%',
    top: '-10%',
    left: '-10%',
    zIndex: 1,
    pointerEvents: 'none',
    opacity: 0.5,
  },
  dashboardCard: {
    position: 'relative',
    zIndex: 10,
    width: '100%',
    maxWidth: '1040px',
    minHeight: '620px',
    backgroundColor: 'rgba(5, 12, 28, 0.65)',
    backdropFilter: 'blur(20px) saturate(160%)',
    border: '1px solid rgba(34, 211, 238, 0.15)',
    boxShadow: '0 24px 50px -12px rgba(0, 0, 0, 0.7), 0 0 40px rgba(6, 182, 212, 0.05)',
    borderRadius: '24px',
    overflow: 'hidden',
    display: 'grid',
    gridTemplateColumns: '1.1fr 0.9fr',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      minHeight: 'auto',
      height: 'fit-content',
    },
  },
  telemetryViewport: {
    position: 'relative',
    backgroundColor: 'rgba(2, 6, 16, 0.4)',
    borderRight: '1px solid rgba(34, 211, 238, 0.08)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  controlPanel: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(5),
    backgroundColor: 'rgba(3, 7, 18, 0.2)',
    position: 'relative',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3, 2.5),
    },
  },
  logoWrapper: {
    textAlign: 'center',
    marginBottom: theme.spacing(4),
    '& img': {
      width: '100%',
      maxWidth: '210px',
      height: 'auto',
      filter: 'drop-shadow(0 0 12px rgba(34, 211, 238, 0.3))',
    }
  },
  inputField: {
    marginBottom: theme.spacing(2),
    '& .MuiFilledInput-root': {
      backgroundColor: 'rgba(15, 23, 42, 0.6)',
      border: '1px solid rgba(56, 189, 248, 0.15)',
      borderRadius: '8px',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        backgroundColor: 'rgba(15, 23, 42, 0.8)',
        borderColor: 'rgba(34, 211, 238, 0.3)',
      },
      '&.Mui-focused': {
        backgroundColor: '#0f172a',
        borderColor: '#22d3ee',
        boxShadow: '0 0 15px rgba(34, 211, 238, 0.15)',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#64748b',
      '&.Mui-focused': {
        color: '#22d3ee',
      }
    },
    '& .MuiFilledInput-input': {
      color: '#f8fafc',
    }
  },
  actionButton: {
    margin: theme.spacing(1.5, 0),
    padding: theme.spacing(1.75),
    borderRadius: '8px',
    background: 'linear-gradient(135deg, #0891b2 0%, #0369a1 100%)',
    boxShadow: '0 4px 14px 0 rgba(8, 145, 178, 0.3)',
    color: '#ffffff',
    fontWeight: 600,
    letterSpacing: '1px',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #22d3ee 0%, #0891b2 100%)',
      boxShadow: '0 6px 20px 0 rgba(34, 211, 238, 0.4)',
      transform: 'translateY(-1px)',
    },
    '&:disabled': {
      background: 'rgba(15, 23, 42, 0.5)',
      color: '#475569',
      border: '1px solid rgba(255,255,255,0.05)',
    }
  },
  utilityRow: {
    marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: theme.spacing(1),
  },
  selectorControl: {
    minWidth: '130px',
    [theme.breakpoints.down('xs')]: {
      minWidth: '115px',
    },
    '& .MuiFilledInput-root': {
      backgroundColor: 'rgba(15, 23, 42, 0.4)',
      borderRadius: '8px',
    },
    '& .MuiInputLabel-root': {
      color: '#64748b',
    },
    '& .MuiSelect-select': {
      color: '#cbd5e1',
    }
  },
  secondaryLink: {
    color: '#64748b',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'color 0.2s ease',
    textDecoration: 'none !important',
    '&:hover': {
      color: '#38bdf8',
    }
  },
  appStoresRow: {
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(3),
    borderTop: '1px solid rgba(255, 255, 255, 0.06)',
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      gap: theme.spacing(1.5),
    }
  },
  storeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#000000',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '10px',
    padding: '8px 16px',
    minWidth: '155px',
    height: '48px',
    textDecoration: 'none !important',
    boxSizing: 'border-box',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    width: 'auto',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      justifyContent: 'center',
    },
    '&:hover': {
      transform: 'translateY(-2px)',
      borderColor: 'rgba(34, 211, 238, 0.4)',
      boxShadow: '0 4px 15px rgba(34, 211, 238, 0.12)',
    }
  },
  storeBadgeIcon: {
    marginRight: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px'
  },
  storeBadgeMeta: {
    display: 'flex',
    flexDirection: 'column',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    lineHeight: 1.2
  },
  storeBadgeTag: {
    fontSize: '9px',
    fontWeight: 500,
    color: '#a1a1aa',
    textTransform: 'uppercase',
    letterSpacing: '0.4px'
  },
  storeBadgeTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#ffffff',
    letterSpacing: '-0.1px'
  },
  loadingContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(2, 4, 8, 0.8)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '24px',
    zIndex: 20,
  }
}));

function VectorNetworkCanvas() {
  return (
      <svg width="100%" height="100%" viewBox="0 0 800 600" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id="skyRadial" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#0f2b5c" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#000000" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect width="800" height="600" fill="url(#skyRadial)" />
        <circle cx="400" cy="300" r="280" stroke="#0e7490" strokeWidth="0.5" strokeDasharray="4 12" opacity="0.3" />
        <circle cx="400" cy="300" r="190" stroke="#1e40af" strokeWidth="0.75" opacity="0.2" />
        <circle cx="400" cy="300" r="90" stroke="#38bdf8" strokeWidth="0.5" strokeDasharray="4 4" opacity="0.25" />
        <line x1="120" y1="300" x2="680" y2="300" stroke="rgba(6,182,212,0.15)" strokeWidth="0.5" />
        <line x1="400" y1="20" x2="400" y2="580" stroke="rgba(6,182,212,0.15)" strokeWidth="0.5" />
      </svg>
  );
}

function TelemetryCoreDisplay() {
  return (
      <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{
            margin: 0,
            fontSize: '32px',
            fontWeight: 700,
            lineHeight: '1.25',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            letterSpacing: '-0.5px',
            background: 'linear-gradient(180deg, #ffffff 0%, #cbd5e1 60%, #64748b 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))'
          }}>
            Pushing the Limits of Smart Mobility.
          </h1>
          <div style={{
            width: '40px',
            height: '3px',
            background: 'linear-gradient(90deg, #22d3ee, transparent)',
            marginTop: '16px',
            borderRadius: '2px',
            boxShadow: '0 0 8px #22d3ee'
          }}></div>
        </div>

        <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '260px' }}>
          <svg viewBox="0 0 440 320" style={{ width: '100%', height: 'auto', overflow: 'visible' }}>
            <defs>
              <style>{`
              @keyframes dashFreight {
                0% { stroke-dashoffset: 240; }
                100% { stroke-dashoffset: 0; }
              }
              @keyframes loopSedan {
                0% { transform: translate(110px, 95px); opacity: 0; }
                10% { opacity: 1; }
                85% { opacity: 1; }
                100% { transform: translate(290px, 185px); opacity: 0; }
              }
              @keyframes loopTruck {
                0% { transform: translate(320px, 85px); opacity: 0; }
                5% { opacity: 1; }
                90% { opacity: 1; }
                100% { transform: translate(140px, 175px); opacity: 0; }
              }
              @keyframes pulseCore {
                0% { transform: scale(0.9); opacity: 0.2; }
                50% { opacity: 0.6; }
                100% { transform: scale(1.4); opacity: 0; }
              }
              .route-freight { stroke-dasharray: 8 16; animation: dashFreight 12s linear infinite; }
              .node-sedan { animation: loopSedan 7s cubic-bezier(0.4, 0, 0.2, 1) infinite; }
              .node-truck { animation: loopTruck 9s cubic-bezier(0.4, 0, 0.2, 1) infinite; }
              .radar-wave { animation: pulseCore 3s ease-out infinite; transform-origin: 220px 140px; }
            `}</style>

              <linearGradient id="netGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgba(34, 211, 238, 0.18)" />
                <stop offset="100%" stopColor="rgba(30, 58, 138, 0.35)" />
              </linearGradient>

              <linearGradient id="headlightCone" x1="0%" y1="50%" x2="100%" y2="50%">
                <stop offset="0%" stopColor="rgba(14, 165, 233, 0.5)" />
                <stop offset="50%" stopColor="rgba(56, 189, 248, 0.2)" />
                <stop offset="100%" stopColor="rgba(56, 189, 248, 0)" />
              </linearGradient>

              <filter id="neon" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="3" result="b" />
                <feComposite in="SourceGraphic" in2="b" operator="over" />
              </filter>
            </defs>

            <g opacity="0.85">
              <path d="M 220 50 L 360 120 L 220 190 L 80 120 Z" fill="url(#netGrad)" stroke="rgba(34,211,238,0.2)" strokeWidth="1.2" />
              <path d="M 220 110 L 360 180 L 220 250 L 80 180 Z" fill="url(#netGrad)" stroke="rgba(30, 58, 138, 0.15)" strokeWidth="1" />
              <line x1="80" y1="120" x2="360" y2="180" stroke="rgba(56,189,248,0.15)" strokeWidth="1" strokeDasharray="4 4" />
              <line x1="220" y1="50" x2="220" y2="250" stroke="rgba(56,189,248,0.15)" strokeWidth="1" />
            </g>

            <g>
              <ellipse cx="220" cy="140" rx="40" ry="20" fill="none" stroke="#22d3ee" strokeWidth="1.5" className="radar-wave" />
              <ellipse cx="220" cy="140" rx="40" ry="20" fill="none" stroke="#3b82f6" strokeWidth="1" className="radar-wave" style={{ animationDelay: '1.5s' }} />
              <ellipse cx="220" cy="140" rx="10" ry="5" fill="none" stroke="#06b6d4" strokeWidth="2" />
            </g>

            <path d="M 110 95 L 290 185" fill="none" stroke="#1e3a8a" strokeWidth="2" opacity="0.4" />
            <path d="M 110 95 L 290 185" fill="none" stroke="#38bdf8" strokeWidth="2" className="route-freight" filter="url(#neon)" />

            <path d="M 320 85 L 140 175" fill="none" stroke="#1e3a8a" strokeWidth="2" opacity="0.4" />
            <path d="M 320 85 L 140 175" fill="none" stroke="#22d3ee" strokeWidth="1.5" className="route-freight" style={{ animationDelay: '-6s' }} />

            <g className="node-sedan">
              <polygon points="0,0 40,35 65,10 0,0" fill="url(#headlightCone)" transform="rotate(22)" />
              <polygon points="0,0 40,-10 55,-40 0,0" fill="url(#headlightCone)" transform="rotate(10)" />
              <polygon points="-14,3 0,10 14,3 0,-4" fill="#60a5fa" />
              <polygon points="-10,-1 0,4 10,-1 0,-6" fill="#3b82f6" />
              <polygon points="-8,-3 0,2 8,-3 0,-8" fill="#dbeafe" />
              <polygon points="-8,-3 0,2 0,5 -8,0" fill="#2563eb" />
              <polygon points="0,2 8,-3 8,0 0,5" fill="#1d4ed8" />
              <ellipse cx="-7" cy="6" rx="2" ry="3" fill="#0f172a" />
              <ellipse cx="7" cy="6" rx="2" ry="3" fill="#0f172a" />
            </g>

            <g className="node-truck">
              <line x1="18" y1="-7" x2="36" y2="-16" stroke="rgba(56, 189, 248, 0.35)" strokeWidth="1.5" />
              <line x1="14" y1="2" x2="30" y2="-6" stroke="rgba(56, 189, 248, 0.25)" strokeWidth="1" />
              <polygon points="-16,5 -55,22 -70,2 -16,5" fill="url(#headlightCone)" transform="rotate(-4)" />
              <polygon points="16,-2 2,-9 -12,-2 2,5" fill="#e2e8f0" />
              <polygon points="16,-2 2,5 2,15 16,8" fill="#94a3b8" />
              <polygon points="2,5 -12,-2 -12,8 2,15" fill="#cbd5e1" />
              <polygon points="14,9 -10,1 -14,3 10,11" fill="#334155" />
              <g transform="translate(-10, 3)">
                <polygon points="2,2 -6,-2 -14,2 -6,6" fill="#38bdf8" />
                <polygon points="2,2 -6,6 -6,13 2,9" fill="#0284c7" />
                <polygon points="-6,6 -14,2 -14,9 -6,13" fill="#0369a1" />
                <polygon points="-4,12 -14,7 -14,9 -4,14" fill="#64748b" />
                <circle cx="-11" cy="9" r="1.2" fill="#e0f2fe" filter="url(#neon)" />
              </g>
              <ellipse cx="10" cy="13" rx="3" ry="4" fill="#1e293b" />
              <ellipse cx="-2" cy="18" rx="3" ry="4" fill="#0f172a" />
              <ellipse cx="-14" cy="14" rx="3" ry="4" fill="#1e293b" />
            </g>

            <g transform="translate(110, 95)" stroke="rgba(34,211,238,0.3)" strokeWidth="0.75" fill="none">
              <line x1="-4" y1="0" x2="4" y2="0" /><line x1="0" y1="-4" x2="0" y2="4" />
            </g>
            <g transform="translate(290, 185)" stroke="rgba(34,211,238,0.3)" strokeWidth="0.75" fill="none">
              <line x1="-4" y1="0" x2="4" y2="0" /><line x1="0" y1="-4" x2="0" y2="4" />
            </g>
          </svg>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          paddingTop: '16px',
          fontFamily: 'monospace',
          fontSize: '11px',
          letterSpacing: '1px'
        }}>
          <div style={{ color: '#06b6d4', fontWeight: 600, opacity: 0.85, textShadow: '0 0 10px rgba(6,182,212,0.3)' }}>
            Powered by TRACTHING
          </div>
          <div style={{ color: '#475569' }}>
            © 2026 TRACTHING
          </div>
        </div>
      </div>
  );
}

export default function LoginForm() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const [errormsg, setErrormsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const registrationEnabled = useSelector(state => state.session.server ? state.session.server['registration'] : false);
  const emailEnabled = useSelector(state => state.session.server ? state.session.server['emailEnabled'] : false);
  const languageSelected = useSelector(state => state.language.language ? state.language.language : 'en');

  const handleEmailChange = (event) => setEmail(event.target.value);
  const handlePasswordChange = (event) => setPassword(event.target.value);
  const handleTogglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    const response = await fetch('/api/session', {
      method: 'POST',
      body: new URLSearchParams(`email=${email}&password=${password}`)
    });
    if (response.ok) {
      const userObject = await response.json();
      const user = userObject.user;
      const tokenThingwings = userObject.token;
      localStorage.setItem("tokenThingwings", tokenThingwings);
      dispatch(sessionActions.updateUser(user));
      setLoading(false);
      history.push('/');
    } else if (response.status === 400) {
      const errorText = await response.text();
      setErrormsg(errorText.split(' ')[0]);
      setLoading(false);
      setPassword('');
    } else {
      setErrormsg('UnknownProblem');
      setLoading(false);
      setPassword('');
    }
  };

  const handleSpecialKey = (e) => {
    if (e.keyCode === 13 && email && password) {
      handleLogin(e);
    }
  };

  const handleForgetPassword = () => {
    history.push('/forgotPassword');
  };

  const settinglanguage = (e) => {
    dispatch(languageActions.updateLanguage(e.target.value));
  };

  return (
      <div className={classes.root}>
        <CssBaseline />

        <div className={classes.backgroundEcosystem}>
          <VectorNetworkCanvas />
        </div>

        <div className={classes.dashboardCard}>
          {/* Left Side: Telemetry Viewport hidden dynamically on mid-to-small tablets & mobile devices */}
          <div className={classes.telemetryViewport}>
            <TelemetryCoreDisplay />
          </div>

          {/* Right Side: Responsive Form Instrument Module Panel */}
          <div className={classes.controlPanel}>
            <div className={classes.logoWrapper}>
              <img src='/logo.png?v=2' alt='Tracthing Dashboard' />
            </div>

            <TextField
                required
                fullWidth
                error={!!errormsg}
                label={t('userEmail')}
                name='email'
                value={email}
                autoComplete='email'
                autoFocus
                onChange={handleEmailChange}
                onKeyUp={handleSpecialKey}
                helperText={errormsg && t(errormsg)}
                variant='filled'
                className={classes.inputField}
                InputProps={{ disableUnderline: true }}
            />

            <TextField
                required
                fullWidth
                error={!!errormsg}
                label={t('userPassword')}
                name='password'
                value={password}
                type={showPassword ? 'text' : 'password'}
                autoComplete='current-password'
                onChange={handlePasswordChange}
                onKeyUp={handleSpecialKey}
                variant='filled'
                className={classes.inputField}
                InputProps={{
                  disableUnderline: true,
                  endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleTogglePasswordVisibility} edge="end" style={{ color: '#64748b' }}>
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                  ),
                }}
            />

            <Button
                onClick={handleLogin}
                onKeyUp={handleSpecialKey}
                variant='contained'
                className={classes.actionButton}
                disabled={!email || !password}
                fullWidth
            >
              {t('loginLogin')}
            </Button>

            <div className={classes.utilityRow}>
              <Button
                  onClick={() => history.push('/register')}
                  disabled={!registrationEnabled}
                  style={{ color: registrationEnabled ? '#38bdf8' : '#475569', fontWeight: 500, textTransform: 'none' }}
              >
                {t('loginRegister')}
              </Button>

              <FormControl variant="filled" className={classes.selectorControl}>
                <InputLabel id="lang-select-label" style={{ fontSize: '13px' }}>{t('loginLanguage')}</InputLabel>
                <Select
                    labelId="lang-select-label"
                    value={languageSelected}
                    onChange={settinglanguage}
                    disableUnderline
                    SelectDisplayProps={{ style: { paddingTop: 20, paddingBottom: 8 } }}
                >
                  {languages.map((it) => (
                      <MenuItem key={it.code} value={it.code}>{it.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {emailEnabled && (
                <div style={{ marginTop: '24px', textAlign: 'center' }}>
                  <Link onClick={handleForgetPassword} className={classes.secondaryLink}>
                    {t('loginReset')}
                  </Link>
                </div>
            )}

            <div className={classes.appStoresRow}>
              {/* App Store Download Badge */}
              <a
                  href="https://apps.apple.com/us/search?term=tracthing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.storeBadge}
              >
                <div className={classes.storeBadgeIcon}>
                  <svg width="18" height="22" viewBox="0 0 170 204" fill="#ffffff">
                    <path d="M150.37 130.25c-.24-28.77 23.36-42.6 24.42-43.29-13.38-19.56-34.19-22.22-41.59-22.53-17.68-1.8-34.56 10.45-43.53 10.45-8.96 0-22.84-10.23-37.62-9.94-19.43.29-37.37 11.35-47.37 28.71-20.21 35.03-5.18 86.85 14.43 115.18 9.6 13.85 20.81 29.27 35.79 28.71 14.43-.57 19.92-9.33 37.37-9.33 17.41 0 22.38 9.33 37.36 9.04 15.26-.29 25.13-13.86 34.66-27.75 11.03-16.12 15.58-31.69 15.84-32.53-.35-.14-30.56-11.72-30.86-46.52zM119.22 38.64c7.93-9.61 13.29-23 11.83-36.36-11.48.46-25.39 7.64-33.64 17.29-7.38 8.47-13.84 22.14-12.09 35.18 12.79 1 25.96-6.51 33.9-16.11z"/>
                  </svg>
                </div>
                <div className={classes.storeBadgeMeta}>
                  <span className={classes.storeBadgeTag}>Download on the</span>
                  <span className={classes.storeBadgeTitle}>App Store</span>
                </div>
              </a>

              {/* Google Play Download Badge */}
              <a
                  href="https://play.google.com/store/search?q=tracthing&c=apps"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.storeBadge}
              >
                <div className={classes.storeBadgeIcon}>
                  <svg width="20" height="22" viewBox="0 0 512 512">
                    <path d="M32.5 12c-3.1 3.5-4.5 9-4.5 16.5v456.9c0 7.5 1.4 13 4.5 16.5L254 256z" fill="#00f0ff"/>
                    <path d="M392.1 113.6L254 256L32.5 12c6.1-4 17.2-5.1 30.1 2.3z" fill="#38bdf8"/>
                    <path d="M392.1 398.4L62.6 501c-12.9 7.3-24 6.2-30.1 2.2z" fill="#0369a1"/>
                    <path d="M479.5 233.1l-87.4-50.5L254 256l138.1 73.4 87.4-50.5c21.2-12.2 21.2-32 0-45.2z" fill="#22d3ee"/>
                  </svg>
                </div>
                <div className={classes.storeBadgeMeta}>
                  <span className={classes.storeBadgeTag}>Get it on</span>
                  <span className={classes.storeBadgeTitle}>Google Play</span>
                </div>
              </a>
            </div>

            {loading && (
                <div className={classes.loadingContainer}>
                  <CircularProgress style={{ color: '#22d3ee' }} />
                </div>
            )}
          </div>
        </div>
      </div>
  );
}